import { useState } from "react";
import { useMutation, useQuery } from "@tanstack/react-query";
import { useLocation, useRoute } from "wouter";
import { getQueryFn, apiRequest, queryClient } from "@/lib/queryClient";
import { ContractForm } from "@/components/admin/commercial/contract-form";
import { BudgetForm } from "@/components/admin/commercial/budget-form";
import { DataTable, type ColumnDef } from "@/components/shared/data-table";
import { Button } from "@/components/ui/button";
import { Card, CardContent, CardHeader, CardTitle } from "@/components/ui/card";
import { Dialog, DialogContent, DialogHeader, DialogTitle } from "@/components/ui/dialog";
import { ArrowLeft, Pencil, Plus } from "lucide-react";
import { formatDate } from "@/lib/format";
import { useToast } from "@/hooks/use-toast";

interface ContractDetail {
  id: string;
  name: string;
  contractType: string;
  status: string;
  renewalMode: string;
  startDate: string;
  endDate: string | null;
  noticePeriodDays: number | null;
  annualPriceReviewMonth: number | null;
  description: string | null;
  client: { id: string; name: string };
  owner: { id: string; email: string; firstName: string | null; lastName: string | null } | null;
  accountManager: { id: string; email: string; firstName: string | null; lastName: string | null } | null;
  budgets: Array<{ id: string; name: string; revenueCategory: string; budgetedAmount: string | null }>;
  renewalEvents: Array<{ id: string; eventType: string; title: string; eventDate: string; status: string }>;
}

export default function ContractDetailPage() {
  const { toast } = useToast();
  const [, navigate] = useLocation();
  const [, params] = useRoute("/contracts/:id");
  const contractId = params?.id;
  const [editOpen, setEditOpen] = useState(false);
  const [budgetOpen, setBudgetOpen] = useState(false);

  const { data: contract, isLoading } = useQuery<ContractDetail>({
    queryKey: ["/api/contracts", contractId],
    queryFn: getQueryFn({ on401: "throw" }),
    enabled: !!contractId,
  });

  const updateMutation = useMutation({
    mutationFn: async (data: Record<string, unknown>) => {
      const response = await apiRequest("PATCH", `/api/contracts/${contractId}`, data);
      return response.json();
    },
    onSuccess: () => {
      queryClient.invalidateQueries({ queryKey: ["/api/contracts"] });
      queryClient.invalidateQueries({ queryKey: ["/api/contracts", contractId] });
      setEditOpen(false);
      toast({ title: "Contract updated" });
    },
  });

  const createBudgetMutation = useMutation({
    mutationFn: async (data: Record<string, unknown>) => {
      const response = await apiRequest("POST", "/api/budgets", data);
      return response.json();
    },
    onSuccess: () => {
      queryClient.invalidateQueries({ queryKey: ["/api/contracts", contractId] });
      queryClient.invalidateQueries({ queryKey: ["/api/budgets"] });
      setBudgetOpen(false);
      toast({ title: "Budget created" });
    },
  });

  const budgetColumns: ColumnDef<ContractDetail["budgets"][number]>[] = [
    {
      key: "name",
      header: "Budget",
      cell: (row) => row.name,
    },
    {
      key: "revenueCategory",
      header: "Revenue",
      cell: (row) => row.revenueCategory.replace(/_/g, " "),
    },
    {
      key: "budgetedAmount",
      header: "Amount",
      cell: (row) => row.budgetedAmount ?? "—",
    },
  ];

  const renewalColumns: ColumnDef<ContractDetail["renewalEvents"][number]>[] = [
    {
      key: "title",
      header: "Event",
      cell: (row) => row.title,
    },
    {
      key: "eventType",
      header: "Type",
      cell: (row) => row.eventType.replace(/_/g, " "),
    },
    {
      key: "eventDate",
      header: "Date",
      cell: (row) => formatDate(row.eventDate),
    },
    {
      key: "status",
      header: "Status",
      cell: (row) => row.status.replace(/_/g, " "),
    },
  ];

  if (!contractId) return null;

  return (
    <div className="space-y-6">
      <Button variant="ghost" onClick={() => navigate("/contracts")}>
        <ArrowLeft className="mr-2 h-4 w-4" />
        Back to contracts
      </Button>

      <div className="flex items-center justify-between gap-4">
        <div>
          <h1 className="text-2xl font-bold">{contract?.name ?? "Contract"}</h1>
          <p className="text-sm text-muted-foreground">
            {contract?.client.name ?? "Loading"} · {contract?.status.replace(/_/g, " ")}
          </p>
        </div>
        <div className="flex gap-2">
          <Button variant="outline" onClick={() => setEditOpen(true)} disabled={!contract}>
            <Pencil className="mr-2 h-4 w-4" />
            Edit
          </Button>
          <Button onClick={() => setBudgetOpen(true)} disabled={!contract}>
            <Plus className="mr-2 h-4 w-4" />
            Add Budget
          </Button>
        </div>
      </div>

      <div className="grid gap-4 lg:grid-cols-3">
        <Card className="lg:col-span-2">
          <CardHeader>
            <CardTitle>Contract Summary</CardTitle>
          </CardHeader>
          <CardContent className="grid gap-3 sm:grid-cols-2">
            <div>
              <p className="text-xs text-muted-foreground">Type</p>
              <p>{contract?.contractType.replace(/_/g, " ") ?? "—"}</p>
            </div>
            <div>
              <p className="text-xs text-muted-foreground">Renewal mode</p>
              <p>{contract?.renewalMode.replace(/_/g, " ") ?? "—"}</p>
            </div>
            <div>
              <p className="text-xs text-muted-foreground">Start date</p>
              <p>{contract ? formatDate(contract.startDate) : "—"}</p>
            </div>
            <div>
              <p className="text-xs text-muted-foreground">End date</p>
              <p>{contract?.endDate ? formatDate(contract.endDate) : "—"}</p>
            </div>
          </CardContent>
        </Card>

        <Card>
          <CardHeader>
            <CardTitle>Ownership</CardTitle>
          </CardHeader>
          <CardContent className="space-y-3">
            <div>
              <p className="text-xs text-muted-foreground">Client</p>
              <p>{contract?.client.name ?? "—"}</p>
            </div>
            <div>
              <p className="text-xs text-muted-foreground">Contract owner</p>
              <p>
                {contract?.owner
                  ? [contract.owner.firstName, contract.owner.lastName].filter(Boolean).join(" ") ||
                    contract.owner.email
                  : "Inherited from account manager"}
              </p>
            </div>
            <div>
              <p className="text-xs text-muted-foreground">Account manager</p>
              <p>
                {contract?.accountManager
                  ? [contract.accountManager.firstName, contract.accountManager.lastName]
                      .filter(Boolean)
                      .join(" ") || contract.accountManager.email
                  : "Unassigned"}
              </p>
            </div>
          </CardContent>
        </Card>
      </div>

      <Card>
        <CardHeader>
          <CardTitle>Budgets</CardTitle>
        </CardHeader>
        <CardContent>
          <DataTable
            columns={budgetColumns}
            data={contract?.budgets}
            isLoading={isLoading}
            rowKey={(row) => row.id}
            onRowClick={(row) => navigate(`/budgets/${row.id}`)}
            emptyTitle="No budgets"
            emptyDescription="Add a budget to structure service items and planned delivery."
          />
        </CardContent>
      </Card>

      <Card>
        <CardHeader>
          <CardTitle>Renewal Events</CardTitle>
        </CardHeader>
        <CardContent>
          <DataTable
            columns={renewalColumns}
            data={contract?.renewalEvents}
            isLoading={isLoading}
            rowKey={(row) => row.id}
            emptyTitle="No renewal events"
            emptyDescription="Events are generated from the contract term settings."
          />
        </CardContent>
      </Card>

      <Dialog open={editOpen} onOpenChange={setEditOpen}>
        <DialogContent className="max-w-2xl">
          <DialogHeader>
            <DialogTitle>Edit Contract</DialogTitle>
          </DialogHeader>
          {contract ? (
            <ContractForm
              defaultValues={{
                clientId: contract.client.id,
                name: contract.name,
                contractType: contract.contractType as any,
                renewalMode: contract.renewalMode as any,
                status: contract.status as any,
                startDate: contract.startDate.slice(0, 10),
                endDate: contract.endDate?.slice(0, 10) ?? "",
                noticePeriodDays: contract.noticePeriodDays ? String(contract.noticePeriodDays) : "",
                annualPriceReviewMonth: contract.annualPriceReviewMonth
                  ? String(contract.annualPriceReviewMonth)
                  : "",
                description: contract.description ?? "",
                contractOwnerUserId: contract.owner?.id ?? "inherit",
              }}
              onSubmit={(data) => updateMutation.mutate(data)}
              isPending={updateMutation.isPending}
            />
          ) : null}
        </DialogContent>
      </Dialog>

      <Dialog open={budgetOpen} onOpenChange={setBudgetOpen}>
        <DialogContent className="max-w-2xl">
          <DialogHeader>
            <DialogTitle>Create Budget</DialogTitle>
          </DialogHeader>
          <BudgetForm
            fixedContractId={contractId}
            onSubmit={(data) => createBudgetMutation.mutate(data)}
            isPending={createBudgetMutation.isPending}
          />
        </DialogContent>
      </Dialog>
    </div>
  );
}
